import React, { type ReactNode } from 'react';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Styleable } from '../../variants';

/**
 * EAA-1821: one table header cell — the label, its sort toggle, and a slot for
 * the column's own filter control.
 *
 * Renders the `<th>` itself rather than its contents, because the two
 * accessibility facts that matter here belong to the CELL, not to anything
 * inside it: `aria-sort` is only meaningful on a header cell, and a
 * sortable-but-unsorted column must say `none` rather than stay silent, so a
 * screen-reader user can tell "sortable" from "not sortable". Hand-built
 * headers get this wrong in one direction or the other every time.
 *
 * Only for hand-built `<table>`s. A screen using `DataTable` gets sorting from
 * antd's own header and should not reach for this.
 */

export type SortDirection = 'asc' | 'desc';

export const columnHeaderCellRecipe = defineRecipe('columnHeaderCell', 'ui-column-header', {
    variants: {
        sortable: { yes: 'ui-column-header--sortable', no: '' },
        sorted: { yes: 'ui-column-header--sorted', no: '' },
    },
    defaultVariants: { sortable: 'no', sorted: 'no' },
});

export interface ColumnHeaderCellProps extends Styleable {
    label: ReactNode;
    /** Direction while THIS column is the sorted one; `null` otherwise. */
    sorted?: SortDirection | null;
    /** Supply to make the label a sort toggle. Absent ⇒ a static header. */
    onSort?: () => void;
    /** The column's `ColumnFilterButton` (inside a `FilterPopover`), or nothing. */
    control?: ReactNode;
    /** Sticky header inside the table's own scroll region. */
    sticky?: boolean;
}

/**
 * A chevron PAIR, not one caret: the inactive halves are what advertise that a
 * column is sortable before anyone clicks, and on the active column the half
 * that is not in play dims to ~28% so DIRECTION is readable without colour
 * alone carrying it.
 */
function SortGlyph({
    sorted,
    color,
    mutedColor,
}: {
    sorted: SortDirection | null;
    color: string;
    mutedColor: string;
}): React.ReactElement {
    const fill = sorted ? color : mutedColor;
    return (
        <svg
            viewBox="0 0 12 16"
            width="9"
            height="12"
            aria-hidden="true"
            className="ui-column-header__sort"
            style={{ flex: 'none' }}
        >
            <path d="M6 1.5 10 6H2z" fill={fill} opacity={sorted && sorted !== 'asc' ? 0.28 : 1} />
            <path
                d="M6 14.5 2 10h8z"
                fill={fill}
                opacity={sorted && sorted !== 'desc' ? 0.28 : 1}
            />
        </svg>
    );
}

export function ColumnHeaderCell({
    label,
    sorted = null,
    onSort,
    control,
    sticky = false,
    className,
    style,
}: ColumnHeaderCellProps): React.ReactElement {
    const colors = useUIColors();
    const sortable = Boolean(onSort);
    const recipeClassName = useRecipe(columnHeaderCellRecipe, {
        sortable: sortable ? 'yes' : 'no',
        sorted: sorted ? 'yes' : 'no',
    });

    let ariaSort: 'ascending' | 'descending' | 'none' | undefined;
    if (sortable) ariaSort = sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : 'none';

    const labelStyle: React.CSSProperties = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: sorted ? colors.primary : 'inherit',
        fontWeight: sorted ? 600 : 500,
    };

    let labelNode: ReactNode = (
        <span className="ui-column-header__text" style={labelStyle}>
            {label}
        </span>
    );
    if (sortable) {
        labelNode = (
            <button
                type="button"
                className="ui-column-header__button"
                onClick={onSort}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    minWidth: 0,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer',
                }}
            >
                <span className="ui-column-header__text" style={labelStyle}>
                    {label}
                </span>
                <SortGlyph sorted={sorted} color={colors.primary} mutedColor={colors.textLight} />
            </button>
        );
    }

    return (
        <th
            scope="col"
            aria-sort={ariaSort}
            className={cx(recipeClassName, className)}
            style={{
                position: sticky ? 'sticky' : undefined,
                top: sticky ? 0 : undefined,
                zIndex: sticky ? 3 : undefined,
                // Sticky headers MUST be opaque — rows scroll underneath them.
                background: sticky ? colors.tableHeaderBg : undefined,
                ...style,
            }}
        >
            <div
                className="ui-column-header__inner"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    minWidth: 0,
                }}
            >
                {labelNode}
                {control}
            </div>
        </th>
    );
}

export default ColumnHeaderCell;
